import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { SectionLabel } from "@/components/Bits";
import { MapPin, ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        organisation: form.organisation.trim() || null,
        message: form.message.trim(),
      });
      setDone(true);
      toast.success("Message sent. We'll be in touch.");
      setForm({ name: "", email: "", organisation: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass =
    "w-full border-b border-[#E5E5E5] bg-transparent py-4 text-[#111111] placeholder:text-[#777777] focus:border-[#111111] focus:outline-none transition-colors";

  return (
    <div className="bg-white">
      <section className="border-b border-[#E5E5E5]">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28">
          <SectionLabel index="01">Get in touch</SectionLabel>
          <h1 className="mt-8 max-w-3xl font-heading font-bold uppercase leading-[0.95] tracking-tighter text-[#111111] text-5xl md:text-7xl">
            Let's talk sport
          </h1>
          <p className="mt-8 max-w-2xl text-[#333333] text-base md:text-lg leading-relaxed">
            Whether you're a club, institution, partner or innovator in this space, send us a
            message and the ZONA Sports team will get back to you.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-24">
        <div className="grid gap-16 lg:grid-cols-12">
          {/* Info */}
          <div className="lg:col-span-4">
            <h2 className="font-heading font-bold uppercase tracking-tight text-2xl text-[#111111]">
              ZONA Sports LTD
            </h2>
            <div className="mt-6 flex items-start gap-3 text-[#333333]">
              <MapPin size={20} className="mt-0.5 text-[#111111]" />
              <div>
                <p className="font-heading text-sm uppercase tracking-[0.16em] text-[#111111]">
                  Liverpool
                </p>
                <p className="text-sm text-[#777777]">United Kingdom</p>
              </div>
            </div>
            <p className="mt-8 text-[#333333] leading-relaxed text-sm">
              We keep our inbox spam-free by handling enquiries through this form. Drop your details
              and we'll reply directly.
            </p>
            <div className="mt-10 border-t border-[#E5E5E5] pt-8">
              <p className="font-heading text-xs uppercase tracking-[0.3em] text-[#777777]">Ethos</p>
              <div className="mt-3 font-heading uppercase tracking-tight text-[#111111] text-lg leading-snug">
                <p>Education</p>
                <p>Infrastructure</p>
                <p>Development</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-8 lg:border-l lg:border-[#E5E5E5] lg:pl-16">
            {done ? (
              <div
                data-testid="contact-success"
                className="flex flex-col items-start gap-5 border border-[#E5E5E5] p-10"
              >
                <CheckCircle2 size={40} className="text-[#111111]" strokeWidth={1.5} />
                <h3 className="font-heading font-bold uppercase tracking-tight text-2xl text-[#111111]">
                  Message received
                </h3>
                <p className="text-[#333333] leading-relaxed">
                  Thank you for reaching out to ZONA Sports. We'll be in touch shortly.
                </p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setDone(false)}
                  className="mt-2 inline-flex items-center gap-2 font-heading text-sm uppercase tracking-[0.16em] text-[#111111] border-b border-[#111111] pb-1"
                >
                  Send another <ArrowUpRight size={16} />
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-10" data-testid="contact-form">
                <div className="grid gap-10 sm:grid-cols-2">
                  <div>
                    <label className="font-heading text-xs uppercase tracking-[0.22em] text-[#777777]">
                      Name *
                    </label>
                    <input
                      data-testid="contact-input-name"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your name"
                      className={fieldClass}
                    />
                  </div>
                  <div>
                    <label className="font-heading text-xs uppercase tracking-[0.22em] text-[#777777]">
                      Email *
                    </label>
                    <input
                      data-testid="contact-input-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@email.com"
                      className={fieldClass}
                    />
                  </div>
                </div>

                <div>
                  <label className="font-heading text-xs uppercase tracking-[0.22em] text-[#777777]">
                    Organisation
                  </label>
                  <input
                    data-testid="contact-input-organisation"
                    value={form.organisation}
                    onChange={update("organisation")}
                    placeholder="Club, company or institution (optional)"
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label className="font-heading text-xs uppercase tracking-[0.22em] text-[#777777]">
                    Message *
                  </label>
                  <textarea
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us how we can help"
                    rows={5}
                    className={`${fieldClass} resize-none`}
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="group inline-flex items-center gap-2 bg-[#111111] px-10 py-4 font-heading text-sm uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#333333] disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Sending
                    </>
                  ) : (
                    <>
                      Send message
                      <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
